import { Option, ReadonlyArray } from "@morphism/fp";
import * as Ant from "antd";
import React from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import { Dashboard } from "~/Dashboard";
import { DVIR } from "~/DVIR";
import { Graph } from "~/Graph";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import { isLoading } from "~/Log/Unassigned";
/* https://app.shortcut.com/esdrive/story/66025/capturing-additional-photos-and-notes-workflow */
/* https://app.shortcut.com/esdrive/story/56676/metric-bar-on-dvir-table */
// import { Summaries as LogSummaries } from "~/Log";
import { Route as GenericRoute } from "~/Route";
import { Time, Moment } from "~/Time";

import { Calendar as Calendar_ } from "./Calendar";
import { Download as Download_ } from "./Download";

import { Query as Query_ } from "./Query";
import { Statistics as Statistics_ } from "./Statistics";
import { Summary as Summary_ } from "./Summary";
import { Table as Table_ } from "./Table";

import { Assets } from "./Table/Assets";
import { Date } from "./Table/Date";
import { Defects } from "./Table/Defects";
import { Driver } from "./Table/Driver";
import { Status } from "./Table/Status";
import { Type } from "./Table/Type";

import { Company } from "../../Company";
import { useWorkReportingLocationFilters } from "~/Driver/Filter";
import { DisabledTable } from "~/DisabledTable";

export type DVIRs = readonly DVIR[];

export function DVIRs() {
  const [mode, setMode] = React.useState<DVIRs.Mode>("table");
  const [interval, setInterval] = DVIRs.Interval.use();
  const [settings, setSettings] = DVIRs.Table.Settings.use();

  const [_wrlLoading, reportingLocationIDs] = useWorkReportingLocationFilters();
  const [
    _branchesLoading,
    branchIDs,
  ] = Company.Branch.Branches.useBranchFiltersFromPreferences();

  const {
    data: summaries,
    loading: summaryLoading,
    errors: summaryErrors,
    warnings: summaryWarnings,
  } = DVIRs.Summary.use({ ...interval, reportingLocationIDs, branchIDs });

  /* https://app.shortcut.com/esdrive/story/66025/capturing-additional-photos-and-notes-workflow */
  /* https://app.shortcut.com/esdrive/story/56676/metric-bar-on-dvir-table */
  // const { shiftsWithDVIRs, shiftsWithNoDVIRs, isLoading: logSummariesLoading } = LogSummaries.use({
  //   ...interval,
  //   missingDVIRs: true,
  // });

  React.useEffect(() => {
    if (summaryWarnings) {
      Graph.Errors.displayMessage(summaryWarnings);
    }
  }, [summaryWarnings]);

  const [isFetchingMore, setIsFetchingMore] = React.useState(true);
  const [lastCursor, setLastCursor] = React.useState('');

  // Fetch the first n records of data for the specified date range.
  const {
    connection,
    dvirs,
    loading,
    fetchMore,
    fetchVariables,
    errors: dvirErrors,
    warnings: dvirWarnings,
  } = DVIRs.Query.useWithMore({
    ...interval,
    reportingLocationIDs,
    branchIDs,
    last: DVIRs.Query.DEFAULT_INITIAL_PAGE_SIZE,
    refetch: true,
  });

  // Display any non fatal errors and ant design's version of a toast message.
  React.useEffect(() => {
    if (dvirWarnings) {
      Graph.Errors.displayMessage(dvirWarnings);
    }
  }, [dvirWarnings]);

  const { pageInfo } = connection;

  // Fetch the remaining data records that are available for the given date range n records at a time, going backward
  // in time from the end date to the start date
  React.useEffect(() => {
    setIsFetchingMore(pageInfo.hasPreviousPage);
    if (
      pageInfo.hasPreviousPage &&
      pageInfo.startCursor &&
      pageInfo.startCursor !== lastCursor
    ) {
      setLastCursor(pageInfo.startCursor);
      setTimeout(() => {
        void fetchMore(
          fetchVariables({
            ...interval,
            reportingLocationIDs,
            branchIDs,
            last: DVIRs.Query.DEFAULT_PAGE_SIZE,
            before: pageInfo.startCursor ?? undefined,
          })
        );
      });
    }
  }, [pageInfo.hasPreviousPage, pageInfo.startCursor]);

  const table = React.useMemo(
    () => (
      <DVIRs.Table
        dvirs={dvirs}
        connection={connection}
        settings={settings}
        setSettings={setSettings}
        isLoading={loading && !fetchMore}
        isFetchingMore={isFetchingMore}
        errors={dvirErrors}
      />
    ),
    // [interval, connection, settings, isFetchingMore]
    [interval, settings, pageInfo]
  );

  const calendar = React.useMemo(
    () => (
      <DVIRs.Calendar
        interval={interval}
        summaries={summaries}
        loading={summaryLoading}
        errors={summaryErrors}
        onNavigate={({ status, ...dateTime }) => {
          setMode("table");
          setSettings({
            status,
            ...Time.DateTime.toDayInterval(dateTime),
          });
        }}
      />
    ),
    [interval, summaries]
  );

  return (
      <Layout.Section.Component>
          <Layout.Section.Header.Component
            Left={() => <DVIRs.Mode mode={mode} onChange={setMode} />}
            Right={() => <DVIRs.Download dvirs={dvirs} settings={settings} />}
          >
            <DVIRs.Interval
            disabled={isLoading()}
            onChange={setInterval}
            {...interval}
          />
          </Layout.Section.Header.Component>

        <div style={{ ...(mode == "calendar" && { display: "none" }) }}>
          {table}
        </div>

        <div style={{ ...(mode == "table" && { display: "none" }) }}>
          {calendar}
        </div>
      </Layout.Section.Component>
  );
}


export function DisabledDVIRsSummary({
  loading = true,
}: {
  readonly loading: boolean
}){
  const [interval, setInterval] = DVIRs.Interval.use();
  const [settings, setSettings] = DVIRs.Table.Settings.use();
  const columns = Table_.columns([], [
    settings,
    setSettings,
  ])

  return (
    <Layout.Section.Component>
        <Layout.Section.Header.Component
          Left={() => <DVIRs.Mode mode={"table"} onChange={()=>{}} />}
          Right={() => <DVIRs.Download dvirs={[]} settings={settings} />}
        >
          <DVIRs.Interval
          disabled={isLoading()}
          onChange={setInterval}
          {...interval}
        />
        </Layout.Section.Header.Component>
        <DisabledTable columns={columns} isLoading={loading} disabledText={"Please select a branch or location filter to view DVIRS"} />
    </Layout.Section.Component>
);
}

export namespace DVIRs {
  export import Calendar = Calendar_;
  export import Download = Download_;
  export import Query = Query_;
  export import Statistics = Statistics_;
  export import Summary = Summary_;
  export import Table = Table_;

  export const emptyConnection = (): DVIR.Connection => ({
    totalCount: 0,
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
    },
    edges: [],
  });

  // This isn't great, because if we add fields to the table, we will have to remember
  // to add their respective filters here as well, but to avoid this we would have
  // to refactor the table, and NOT use controlled mode - not worth it right now IMO.

  // In UNcontrolled mode, each filter change fires 'onChange' at the top level of the
  // table, where we can access the table's already filtered data. Ideally we'd use that for
  // export
  export const applyAllFilters = (
    dvirs: readonly DVIR[],
    settings: DVIRs.Table.Settings
  ) =>
    dvirs
      // Assets - vehicle + trailers
      .filter(Assets.getFilterFuncForSettings("vehicle", settings))
      .filter(Assets.getFilterFuncForSettings("trailers", settings))
      // Date
      .filter(Date.getFilterFuncForSettings(settings))
      // Defects - vehicle + trailers
      .filter(Defects.getFilterFuncForSettings("vehicle", settings))
      .filter(Defects.getFilterFuncForSettings("trailers", settings))
      // Drivers
      .filter(Driver.getFilterFuncForSettings(settings))
      // Status
      .filter(Status.getFilterFuncForSettings(settings))
      // Type
      .filter(Type.getFilterFuncForSettings(settings));

  export namespace Route {
    export namespace WithoutInterval {
      export const definition = () => "/dvirs";
      export const href = definition;
      export const use = () => GenericRoute.use(href());
    }

    export namespace WithInterval {
      export type Params = { readonly start?: string; readonly stop?: string };

      export const definition = () =>
        `${WithoutInterval.definition()}/:start/:stop`;

      export const href = (interval: Time.Interval.Stopped) =>
        pipe(
          [interval.interval.start, interval.interval.stop],
          ReadonlyArray.map(Time.DateTime.toISOLocalWithoutTime),
          ([start, stop]) => `${WithoutInterval.definition()}/${start}/${stop}`
        );

      export const use = (interval: Time.Interval.Stopped) =>
        GenericRoute.use(href(interval));

      export const useInterval = () =>
        pipe(
          useRouteMatch<Params>(Route.WithInterval.definition()),
          Option.fromNullable,
          Option.chain(({ params: { start, stop } }) =>
            start && stop ? Option.some({ start, stop }) : Option.none
          ),
          Option.chain(({ start, stop }) =>
            pipe(
              Time.Interval.Stopped.fromDateStrings(start)(stop),
              Option.map(({ interval }) => ({
                interval: {
                  start: Time.DateTime.toStartOfDayForZone(interval.start),
                  stop: Time.DateTime.toEndOfDayForZone(interval.stop),
                },
              }))
            )
          )
        );
    }
  }
  export namespace Loading {
    export const Container = styled.div`
      z-index: 10;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      display: flex;
      align-items: center;
      justify-content: center;

      opacity: 0.8;
      background: white;

      text-align: center;
    `;
  }

  export function Interval({
    disabled,
    interval,
    onChange,
  }: Time.Interval.Stopped & {
    readonly disabled?: boolean;
    readonly onChange: (interval: Time.Interval.Stopped) => void;
  }) {
    return (
      <Ant.DatePicker.RangePicker
        disabled={disabled}
        format="MM/DD/YYYY"
        allowClear={false}
        allowEmpty={[false, false]}
        defaultValue={[
          Moment.default(interval.start.dateTime),
          Moment.default(interval.stop.dateTime),
        ]}
        onChange={(interval) =>
          interval?.[0] &&
          interval[1] &&
          onChange(
            Time.Interval.Stopped.fromDateTimes(
              Time.DateTime.toStartOfDayForZone(
                Time.DateTime.fromNumber(interval[0].valueOf())
              ),
              Time.DateTime.toEndOfDayForZone(
                Time.DateTime.fromNumber(interval[1].valueOf())
              )
            )
          )
        }
      />
    );
  }

  export namespace Interval {
    export const initial = () => Dashboard.Interval.initial();

    export const use = () => {
      const history = useHistory();

      const intervalFromRoute = Option.toUndefined(
        Route.WithInterval.useInterval()
      );

      const interval = intervalFromRoute ?? initial();
      const route = Route.WithInterval.use(interval);

      React.useEffect(() => {
        !intervalFromRoute && route.go();
      }, [interval]);

      return [
        interval,
        (interval: Time.Interval.Stopped) =>
          history.push(Route.WithInterval.href(interval)),
      ] as const;
    };
  }

  export type Mode = "table" | "calendar";
  export const Mode = ({
    mode,
    onChange,
  }: {
    readonly mode: Mode;
    readonly onChange: (mode: Mode) => void;
  }) => (
    <Ant.Radio.Group
      value={mode}
      onChange={(event) =>
        (event.target.value === "table" || event.target.value === "calendar") &&
        onChange(event.target.value)
      }
    >
      <Ant.Radio.Button value="table">
        <Layout.Space>
          <Icon.Table />
          Table
        </Layout.Space>
      </Ant.Radio.Button>
      <Ant.Radio.Button value="calendar">
        <Layout.Space>
          <Icon.Calendar />
          Calendar
        </Layout.Space>
      </Ant.Radio.Button>
    </Ant.Radio.Group>
  );
}
